import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { LANDING_FEATURES } from "../utils/landingFeatures";
import { track } from "../utils/analytics";

interface Props {
  /** Light = white text on gradient navbar. Dark = dark text on light surfaces. */
  variant?: "light" | "dark";
  /** Mobile menus inline-expand instead of using a floating popover. */
  inline?: boolean;
  onNavigate?: () => void;
}

export default function LandingProductsMenu({ variant = "light", inline = false, onNavigate }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const triggerClass =
    variant === "light"
      ? "inline-flex items-center gap-1.5 text-sm font-semibold text-white/95 hover:text-white px-3 py-2 rounded-full hover:bg-white/10 transition-colors"
      : "inline-flex items-center gap-1.5 text-sm font-semibold text-[#1e1b3a] hover:text-violet-700 px-3 py-2 rounded-full hover:bg-violet-50 transition-colors";

  const handleItemClick = (slug: string) => {
    track("landing_products_menu_clicked", { feature: slug });
    setOpen(false);
    onNavigate?.();
  };

  const items = (
    <ul className={inline ? "space-y-1" : "p-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5"}>
      {LANDING_FEATURES.map((f) => {
        const Icon = f.icon;
        return (
          <li key={f.slug}>
            <Link
              to={`/features/${f.slug}`}
              onClick={() => handleItemClick(f.slug)}
              className="group flex items-start gap-3 p-3 rounded-xl hover:bg-violet-50 transition-colors"
            >
              <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${f.iconBg} text-white flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform`}>
                <Icon size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-extrabold text-[14px] text-[#1e1b3a] truncate">
                    {f.navLabel}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-violet-400 -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                </div>
                <p className="text-[12px] text-[#3f3a52]/75 leading-snug mt-0.5 line-clamp-2">
                  {f.navDescription}
                </p>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );

  if (inline) {
    return (
      <div className="w-full">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl ${
            variant === "light"
              ? "text-white/95 hover:bg-white/10"
              : "text-[#1e1b3a] hover:bg-violet-50"
          } font-semibold text-sm`}
          aria-expanded={open}
        >
          <span>Products</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <div
          className={`grid transition-all duration-200 ${
            open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            <div className="bg-white/95 backdrop-blur-md rounded-2xl mt-1 mx-1 p-1.5 border border-white/20 shadow-xl">
              {items}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={triggerClass}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>Products</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <div
        className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 transition-all duration-200 origin-top ${
          open
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-95 pointer-events-none"
        }`}
        role="menu"
      >
        <div className="w-[min(92vw,580px)] bg-white rounded-2xl shadow-[0_25px_70px_-15px_rgba(76,29,149,0.45)] border border-violet-100 overflow-hidden">
          <div className="px-4 py-2.5 bg-gradient-to-r from-violet-50 to-fuchsia-50 border-b border-violet-100">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-violet-700">
              The DealHawk Toolkit
            </div>
            <div className="text-[12px] text-[#3f3a52]/70 mt-0.5">
              Everything our members use to find markdowns first.
            </div>
          </div>
          {items}
          <div className="px-3 pb-3">
            <Link
              to="/get-started"
              onClick={() => handleItemClick("__cta")}
              className="flex items-center justify-center gap-1.5 w-full h-11 rounded-xl bg-[#1e1b3a] hover:bg-black text-white font-extrabold text-sm transition-colors"
            >
              Get started free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
